import React, { useState } from 'react';
import {
    Drawer, Descriptions, Tag, Space, Typography, Divider, Table, Button,
    Input, List, Avatar, Popconfirm, message, Tooltip, Empty, Select, Upload,
    InputNumber,
} from 'antd';
import {
    FileOutlined, DownloadOutlined, DeleteOutlined, SendOutlined,
    UserOutlined, LinkOutlined, InboxOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeApi, filesApi } from '../../../api/client';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const COST_CATEGORIES: Record<string, string> = {
    PROCUREMENT: 'Zaopatrzenie', COGS: 'Koszt sprzedanych towarów', MARKETING: 'Marketing',
    OPERATIONS: 'Operacje', PERSONNEL: 'Kadry', TECHNOLOGY: 'Technologia',
    LOGISTICS: 'Logistyka', LEGAL: 'Prawne', OTHER: 'Inne',
};

const PAYMENT_COLORS: Record<string, string> = {
    UNPAID: 'red', PARTIALLY_PAID: 'orange', PAID: 'green', OVERDUE: 'volcano',
};

const PAYMENT_LABELS: Record<string, string> = {
    UNPAID: 'Nieopłacone', PARTIALLY_PAID: 'Częściowo', PAID: 'Opłacone', OVERDUE: 'Przeterminowane',
};

interface CostDetailDrawerProps {
    costId: string | null;
    onClose: () => void;
}

const CostDetailDrawer: React.FC<CostDetailDrawerProps> = ({ costId, onClose }) => {
    const [commentText, setCommentText] = useState('');
    const queryClient = useQueryClient();

    // Fetch cost
    const { data: cost } = useQuery({
        queryKey: ['cost-detail', costId],
        queryFn: async () => (await financeApi.getCost(costId!)).data,
        enabled: !!costId,
    });

    // Fetch line items
    const { data: lineItems = [] } = useQuery({
        queryKey: ['cost-line-items', costId],
        queryFn: async () => (await financeApi.getCostLineItems(costId!)).data,
        enabled: !!costId,
    });

    // Fetch files
    const { data: files = [] } = useQuery({
        queryKey: ['cost-files', costId],
        queryFn: async () => (await filesApi.getByEntity('COST', costId!)).data,
        enabled: !!costId,
    });

    // Fetch comments
    const { data: comments = [] } = useQuery({
        queryKey: ['cost-comments', costId],
        queryFn: async () => (await financeApi.getCostComments(costId!)).data,
        enabled: !!costId,
    });

    // Comment mutation
    const addCommentMutation = useMutation({
        mutationFn: (data: { content: string }) => financeApi.createCostComment(costId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-comments', costId] });
            setCommentText('');
        },
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => financeApi.deleteCostComment(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cost-comments', costId] }),
    });

    const deleteFileMutation = useMutation({
        mutationFn: (id: string) => filesApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-files', costId] });
            message.success('Plik usunięty');
        },
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => financeApi.updateCost(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-detail', costId] });
            queryClient.invalidateQueries({ queryKey: ['costs'] });
            queryClient.invalidateQueries({ queryKey: ['costs-overview'] });
            message.success('Status zaktualizowany');
        },
    });

    const uploadFileMutation = useMutation({
        mutationFn: (file: File) => filesApi.upload(file, 'COST', costId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cost-files', costId] });
            message.success('Plik wgrany');
        },
    });

    if (!costId) return null;

    const lineItemColumns = [
        { title: 'Pozycja', dataIndex: 'name', key: 'name' },
        { title: 'Ilość', dataIndex: 'quantity', key: 'qty', width: 60, render: (v: number) => Number(v).toFixed(2) },
        { title: 'Cena netto', dataIndex: 'unitPriceNet', key: 'upn', width: 100, render: (v: number) => `${Number(v).toFixed(2)} zł` },
        { title: 'VAT', dataIndex: 'vatRate', key: 'vat', width: 60, render: (v: number) => v < 0 ? (v === -1 ? 'zw.' : 'np.') : `${v}%` },
        { title: 'Rabat', key: 'disc', width: 80, render: (_: any, r: any) => r.discountType ? `${r.discountType === 'PERCENT' ? `${r.discountValue}%` : `${Number(r.discountValue).toFixed(2)} zł`}` : '—' },
        { title: 'Netto', dataIndex: 'netAmount', key: 'net', width: 100, render: (v: number) => `${Number(v).toFixed(2)} zł` },
        { title: 'VAT kwota', dataIndex: 'vatAmount', key: 'vatAmt', width: 80, render: (v: number) => `${Number(v).toFixed(2)} zł` },
        { title: 'Brutto', dataIndex: 'grossAmount', key: 'gross', width: 100, render: (v: number) => <Text strong>{Number(v).toFixed(2)} zł</Text> },
    ];

    return (
        <Drawer
            title={cost?.name || 'Szczegóły kosztu'}
            open={!!costId}
            onClose={onClose}
            width={700}
            destroyOnClose
        >
            {cost && (
                <>
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="Nazwa">{cost.name}</Descriptions.Item>
                        <Descriptions.Item label="Dostawca">{cost.vendorName || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Data">{cost.date ? dayjs(cost.date).format('DD.MM.YYYY') : '—'}</Descriptions.Item>
                        <Descriptions.Item label="Termin">{cost.dueDate ? dayjs(cost.dueDate).format('DD.MM.YYYY') : '—'}</Descriptions.Item>
                        <Descriptions.Item label="Kategoria"><Tag>{COST_CATEGORIES[cost.category] || cost.category}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <div>
                                <Select
                                    value={cost.paymentStatus}
                                    size="small"
                                    style={{ width: 160 }}
                                    onChange={(v) => updateStatusMutation.mutate({ id: costId!, data: { paymentStatus: v } })}
                                    loading={updateStatusMutation.isPending}
                                >
                                    <Select.Option value="UNPAID"><Tag color="red">Nieopłacone</Tag></Select.Option>
                                    <Select.Option value="PARTIALLY_PAID"><Tag color="orange">Częściowo</Tag></Select.Option>
                                    <Select.Option value="PAID"><Tag color="green">Opłacone</Tag></Select.Option>
                                    <Select.Option value="OVERDUE"><Tag color="volcano">Przeterminowane</Tag></Select.Option>
                                </Select>
                                {cost.paymentStatus === 'PARTIALLY_PAID' && (
                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Kwota wpłacona:</Text>
                                        <InputNumber
                                            value={cost.paidAmount != null ? Number(cost.paidAmount) : 0}
                                            size="small"
                                            min={0}
                                            precision={2}
                                            decimalSeparator=","
                                            addonAfter="PLN"
                                            style={{ width: 160 }}
                                            onBlur={(e) => {
                                                const val = parseFloat(e.target.value?.replace(',', '.') || '0');
                                                updateStatusMutation.mutate({ id: costId!, data: { paidAmount: val } });
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Descriptions.Item>
                        <Descriptions.Item label="Nr faktury">{cost.invoiceNumber || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Typ">{cost.costType === 'RECURRING' ? 'Cykliczny' : 'Jednorazowy'}</Descriptions.Item>
                        <Descriptions.Item label="Kwota netto">{cost.totalNet != null ? `${Number(cost.totalNet).toFixed(2)} PLN` : '—'}</Descriptions.Item>
                        <Descriptions.Item label="Kwota brutto"><Text strong>{Number(cost.amount || 0).toFixed(2)} PLN</Text></Descriptions.Item>
                        {cost.budgetPositionName && (
                            <Descriptions.Item label="Pozycja budżetowa" span={2}>
                                <Tag icon={<LinkOutlined />} color="blue">
                                    {cost.budgetPositionName} {cost.budgetPositionCategory ? `(${cost.budgetPositionCategory})` : ''}
                                </Tag>
                            </Descriptions.Item>
                        )}
                        {cost.costPurposes && cost.costPurposes.length > 0 && (
                            <Descriptions.Item label="Cel kosztów" span={2}>
                                {cost.costPurposes.map((p: string) => <Tag key={p} color="purple">#{p}</Tag>)}
                            </Descriptions.Item>
                        )}
                        {cost.notes && <Descriptions.Item label="Notatki" span={2}>{cost.notes}</Descriptions.Item>}
                    </Descriptions>

                    {/* Line Items */}
                    {lineItems.length > 0 && (
                        <>
                            <Divider orientation="left">Pozycje kosztowe ({lineItems.length})</Divider>
                            <Table
                                dataSource={lineItems}
                                columns={lineItemColumns}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                summary={() => (
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={5}><Text strong>RAZEM</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell index={5}><Text strong>{lineItems.reduce((s: number, i: any) => s + Number(i.netAmount), 0).toFixed(2)} zł</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell index={6}><Text>{lineItems.reduce((s: number, i: any) => s + Number(i.vatAmount), 0).toFixed(2)} zł</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell index={7}><Text strong>{lineItems.reduce((s: number, i: any) => s + Number(i.grossAmount), 0).toFixed(2)} zł</Text></Table.Summary.Cell>
                                    </Table.Summary.Row>
                                )}
                            />
                        </>
                    )}

                    {/* Attached files / invoices */}
                    <Divider orientation="left">Załączniki ({files.length})</Divider>
                    <Upload.Dragger
                        showUploadList={false}
                        beforeUpload={(file) => {
                            uploadFileMutation.mutate(file);
                            return false;
                        }}
                        accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                        style={{ marginBottom: 12, padding: '4px 0' }}
                        height={80}
                    >
                        <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}><InboxOutlined style={{ fontSize: 24 }} /></p>
                        <p className="ant-upload-text" style={{ fontSize: 13, margin: 0 }}>Przeciągnij plik lub kliknij</p>
                    </Upload.Dragger>
                    {files.length > 0 && (
                        <List
                            size="small"
                            dataSource={files}
                            renderItem={(file: any) => (
                                <List.Item
                                    actions={[
                                        <Tooltip title="Pobierz" key="dl">
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={() => {
                                                    const authStore = localStorage.getItem('curecraft-auth');
                                                    let token = '';
                                                    if (authStore) try { token = JSON.parse(authStore).state?.token || ''; } catch { }
                                                    window.open(`${filesApi.getDownloadUrl(file.id)}?token=${token}`, '_blank');
                                                }}
                                            />
                                        </Tooltip>,
                                        <Popconfirm key="del" title="Usunąć plik?" onConfirm={() => deleteFileMutation.mutate(file.id)}>
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<FileOutlined style={{ fontSize: 20 }} />}
                                        title={file.originalName}
                                        description={`${(file.size / 1024).toFixed(1)} KB • ${dayjs(file.createdAt).format('DD.MM.YYYY HH:mm')}`}
                                    />
                                </List.Item>
                            )}
                        />
                    )}

                    {/* Comments */}
                    <Divider orientation="left">Komentarze ({comments.length})</Divider>
                    {comments.length === 0 && <Empty description="Brak komentarzy" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    {comments.length > 0 && (
                        <List
                            size="small"
                            dataSource={comments}
                            renderItem={(comment: any) => (
                                <List.Item
                                    actions={[
                                        <Popconfirm key="del" title="Usunąć?" onConfirm={() => deleteCommentMutation.mutate(comment.id)}>
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<UserOutlined />} size="small" />}
                                        title={
                                            <Space>
                                                <Text strong>{comment.createdBy?.firstName || 'Użytkownik'} {comment.createdBy?.lastName || ''}</Text>
                                                <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(comment.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                                            </Space>
                                        }
                                        description={comment.content}
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                    <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                        <Input
                            placeholder="Dodaj komentarz..."
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            onPressEnter={() => {
                                if (commentText.trim()) addCommentMutation.mutate({ content: commentText.trim() });
                            }}
                        />
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={addCommentMutation.isPending}
                            onClick={() => {
                                if (commentText.trim()) addCommentMutation.mutate({ content: commentText.trim() });
                            }}
                        />
                    </Space.Compact>
                </>
            )}
        </Drawer>
    );
};

export default CostDetailDrawer;
